import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DescriptionItem,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  StatusPill,
  TableWrap,
  Td,
  Textarea,
  Th,
  Tr
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import {
  formatDate,
  formatDateTime,
  formatNumber,
  subscriptionStatusLabels,
  subscriptionStatusTone,
  transactionTypeLabels,
  transactionTypeTone
} from '../../components/admin/labels';
import { IconCard } from '../../components/admin/icons';
import { UpgradeGate } from '../../components/courier/ui';
import { useCourierSession } from '../../components/courier/CourierLayout';
import { fieldErrorsFrom } from '../../utils/formErrors';

const TABS = [
  { id: 'plan', label: 'Plan' },
  { id: 'billing', label: 'Billing history' },
  { id: 'details', label: 'Details' }
];

/**
 * Plan, payments and account details.
 *
 * Mirrors the merchant account page, including the cancellation flow: a
 * courier raises a request with a reason rather than switching their own
 * subscription off, because the Stripe billing portal has cancelling disabled
 * and somebody at ParcelBuddy should see why before anything stops.
 */
const CourierAccount = () => {
  const toast = useToast();
  const { user, me, refreshMe, fullAccess } = useCourierSession() || {};

  // The open tab lives in the URL so a reload keeps it — the same treatment
  // the merchant account page gets.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab = TABS.some((t) => t.id === requested) ? requested : 'plan';
  const setTab = (id) => setSearchParams(id === 'plan' ? {} : { tab: id }, { replace: true });

  const [transactions, setTransactions] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelForm, setCancelForm] = useState({ timing: 'end_of_period', note: '' });
  const [cancelErrors, setCancelErrors] = useState({});
  const [cancelling, setCancelling] = useState(false);

  const account = me?.account;
  const subscription = me?.subscription;
  const pendingCancellation = me?.cancellationRequest?.status === 'pending';
  const plans = me?.pricing?.courierPlanPrices || {};
  const intelligence = plans.intelligence || {};

  const loadTransactions = useCallback(async () => {
    try {
      const { data } = await api.get('/api/courier-portal/transactions');
      setTransactions(data || []);
    } catch (error) {
      setTransactions([]);
    }
  }, []);

  useEffect(() => {
    if (tab === 'billing' && transactions === null) loadTransactions();
  }, [tab, transactions, loadTransactions]);

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await api.post('/api/courier-portal/billing-portal');
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast('Could not open the billing portal.', { tone: 'error' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not open the billing portal.', { tone: 'error' });
    } finally {
      setOpeningPortal(false);
    }
  };

  const submitCancellation = async (e) => {
    e.preventDefault();
    setCancelling(true);
    setCancelErrors({});
    try {
      await api.post('/api/courier-portal/cancellation-request', cancelForm);
      setCancelOpen(false);
      setCancelForm({ timing: 'end_of_period', note: '' });
      await refreshMe?.();
      toast('Request sent. Nothing has changed yet — we will be in touch.', { tone: 'success' });
    } catch (error) {
      setCancelErrors(fieldErrorsFrom(error, { fallback: 'Could not send your request.' }));
    } finally {
      setCancelling(false);
    }
  };

  if (!me) {
    return (
      <Card>
        <CardBody>
          <Skeleton className="h-24 w-full" />
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-1 rounded-xl bg-ink-100 p-1" role="tablist">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`account-tab-${t.id}`}
              aria-selected={active}
              aria-controls={`account-panel-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-lg px-4 py-2 text-sm font-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-600 ${
                active ? 'bg-white text-info-700 shadow-card' : 'text-ink-600 hover:text-ink-900'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'plan' && (
        <div id="account-panel-plan" role="tabpanel" aria-labelledby="account-tab-plan" className="space-y-5">
          {fullAccess ? (
            <Card>
              <CardHeader
                title="Intelligence"
                description="Full access to the reports, retailers and hotspots behind your figures."
                actions={
                  <StatusPill tone={subscriptionStatusTone[subscription?.status] || 'neutral'}>
                    {subscriptionStatusLabels[subscription?.status] || subscription?.status || 'Active'}
                  </StatusPill>
                }
              />
              <CardBody className="space-y-5">
                <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-3">
                  <DescriptionItem label="Price">
                    {intelligence.price != null ? `£${formatNumber(intelligence.price)} / month` : '—'}
                  </DescriptionItem>
                  <DescriptionItem label="Renews">
                    {subscription?.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : '—'}
                  </DescriptionItem>
                  <DescriptionItem label="Carrier">{account?.courier || 'Not linked yet'}</DescriptionItem>
                </dl>

                {pendingCancellation ? (
                  // The button is replaced, not just disabled: a second
                  // request would tell them nothing and create a second thing
                  // for an admin to resolve.
                  <div className="rounded-xl bg-warning-50 p-4 ring-1 ring-inset ring-warning-100">
                    <p className="text-sm font-600 text-ink-900">Cancellation requested</p>
                    <p className="mt-1 text-sm leading-relaxed text-ink-600">
                      You asked to cancel{' '}
                      {me.cancellationRequest.timing === 'immediate'
                        ? 'as soon as possible'
                        : 'at the end of the billing period'}
                      {me.cancellationRequest.requestedAt &&
                        ` on ${formatDate(me.cancellationRequest.requestedAt)}`}
                      . Your access continues until we action it — we will be in touch.
                    </p>
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-2">
                  {subscription?.canManagePayment && (
                    <Button
                      variant="secondary"
                      loading={openingPortal}
                      disabled={openingPortal}
                      onClick={openBillingPortal}
                    >
                      <IconCard size={16} />
                      Update payment method
                    </Button>
                  )}
                  {!pendingCancellation && (
                    <Button variant="dangerGhost" onClick={() => setCancelOpen(true)}>
                      Request cancellation
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader title="Pilot" description="Free, with the headline figures on your dashboard." />
                <CardBody>
                  <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-3">
                    <DescriptionItem label="Free cases used">
                      {account?.freeCaseAllowance != null
                        ? `${formatNumber(account.pilotCasesUsed || 0)} of ${formatNumber(account.freeCaseAllowance)}`
                        : formatNumber(account?.pilotCasesUsed || 0)}
                    </DescriptionItem>
                    <DescriptionItem label="Carrier">
                      {account?.courier || 'Not linked yet'}
                    </DescriptionItem>
                    <DescriptionItem label="Card on file">None</DescriptionItem>
                  </dl>
                </CardBody>
              </Card>

              <UpgradeGate
                title={subscription?.status === 'canceled' ? 'Resubscribe to Intelligence' : 'Upgrade to Intelligence'}
                description="Every report naming your network, the retailers behind them and the postcode areas they come from."
                features={intelligence.features || []}
                price={intelligence.price ?? null}
              />
            </>
          )}
        </div>
      )}

      {tab === 'billing' && (
        <div id="account-panel-billing" role="tabpanel" aria-labelledby="account-tab-billing">
          <Card>
            <CardHeader
              title="Billing history"
              description="Every payment taken for this account."
              actions={
                subscription?.canManagePayment && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={openingPortal}
                    disabled={openingPortal}
                    onClick={openBillingPortal}
                  >
                    <IconCard size={15} />
                    Payment method
                  </Button>
                )
              }
            />
            {transactions === null ? (
              <CardBody>
                <Skeleton className="h-20 w-full" />
              </CardBody>
            ) : transactions.length === 0 ? (
              <EmptyState
                icon={IconCard}
                title="Nothing billed yet"
                description="Payments appear here as soon as your first subscription charge goes through."
              />
            ) : (
              <TableWrap>
                <thead>
                  <tr>
                    <Th>Type</Th>
                    <Th>Description</Th>
                    <Th className="text-right">Amount</Th>
                    <Th>Date</Th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <Tr key={t._id}>
                      <Td>
                        <StatusPill tone={transactionTypeTone[t.type] || 'neutral'}>
                          {transactionTypeLabels[t.type] || t.type}
                        </StatusPill>
                      </Td>
                      <Td className="text-ink-700">{t.description || '—'}</Td>
                      <Td className="whitespace-nowrap text-right font-600 tabular-nums text-ink-900">
                        £{Number(t.amount || 0).toFixed(2)}
                      </Td>
                      <Td className="whitespace-nowrap text-ink-500">{formatDateTime(t.occurredAt)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </Card>
        </div>
      )}

      {tab === 'details' && (
        <div id="account-panel-details" role="tabpanel" aria-labelledby="account-tab-details">
          <Card>
            <CardHeader
              title="Account details"
              description="Held by ParcelBuddy — get in touch to change any of it."
            />
            <CardBody>
              <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                <DescriptionItem label="Company">{account?.name || '—'}</DescriptionItem>
                <DescriptionItem label="Carrier shown">
                  {account?.courier || 'Not linked yet'}
                </DescriptionItem>
                <DescriptionItem label="Signed in as">{user?.name || '—'}</DescriptionItem>
                <DescriptionItem label="Email">{user?.email || '—'}</DescriptionItem>
              </dl>
            </CardBody>
          </Card>
        </div>
      )}

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Request cancellation"
        description="Nothing is cancelled right away. We read every request, and will confirm before your access ends."
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              Keep my plan
            </Button>
            <Button
              variant="danger"
              form="courier-cancel-form"
              type="submit"
              loading={cancelling}
              disabled={cancelling}
            >
              Send request
            </Button>
          </>
        }
      >
        <form id="courier-cancel-form" onSubmit={submitCancellation} className="space-y-4" noValidate>
          {cancelErrors._general && (
            <div
              role="alert"
              className="rounded-lg bg-danger-50 px-3.5 py-3 text-sm text-danger-700 ring-1 ring-inset ring-danger-100"
            >
              {cancelErrors._general}
            </div>
          )}

          <Field label="When" htmlFor="cancel-timing" required error={cancelErrors.timing}>
            <Select
              id="cancel-timing"
              value={cancelForm.timing}
              onChange={(e) => setCancelForm({ ...cancelForm, timing: e.target.value })}
            >
              <option value="end_of_period">At the end of the period I have paid for</option>
              <option value="immediate">As soon as possible</option>
            </Select>
          </Field>

          <Field
            label="Why are you cancelling?"
            htmlFor="cancel-note"
            required
            error={cancelErrors.note}
            hint="This goes to the ParcelBuddy team, not an automated system."
          >
            <Textarea
              id="cancel-note"
              value={cancelForm.note}
              onChange={(e) => setCancelForm({ ...cancelForm, note: e.target.value })}
              placeholder="Tell us what isn't working"
            />
          </Field>
        </form>
      </Modal>
    </div>
  );
};

export default CourierAccount;
